import React from 'react';
import { toast } from 'react-toastify';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchPost } from '../../store/fetch';
import { Modal } from '../common/Modal/Modal';
import {
    AlertMicro,
    EditPanelButton,
    EditPanelCell,
    EditPanelHeader,
    EditPanelInputPassword,
    EditPanelLabel,
    EditPanelRow,
    EditPanelSection,
    PanelButtonLink,
} from '../elements';

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ChangePassword(props: { passwordAge?: undefined | number }) {
    const translate = useTranslate();
    const passwordAge = props.passwordAge ?? 0;
    const [isChangePasswordDialogVisible, setIsChangePasswordDialogVisible] = React.useState(false);

    return (
        <>
            <EditPanelHeader text={translate('common.password')}></EditPanelHeader>

            <EditPanelRow>
                <EditPanelLabel rowCount={6}>{translate('profile.passwordAge', passwordAge.toString())}</EditPanelLabel>
                <EditPanelCell rowCount={6}>
                    <PanelButtonLink
                        text={translate('profile.changePassword')}
                        onClick={() => setIsChangePasswordDialogVisible(true)}
                        small
                        primary={passwordAge > 182 ? true : false}
                        mt
                        icon="fa fa-key"
                    ></PanelButtonLink>
                </EditPanelCell>
            </EditPanelRow>

            {isChangePasswordDialogVisible && <PasswordDialog onClose={() => setIsChangePasswordDialogVisible(false)} />}
        </>
    );
}

function PasswordDialog(props: { onClose: () => void }) {
    const translate = useTranslate();
    const [password, setPassword] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [newPassword, setNewPassword] = React.useState('');
    const [newPasswordRepeat, setNewPasswordRepeat] = React.useState('');
    const [newPasswordValid, setNewPasswordValid] = React.useState({ valid: true, message: '' });
    const [currentPasswordValid, setCurrentPasswordValid] = React.useState({ valid: true, message: '' });

    const handleCloseFormClicked = () => {
        props.onClose();
    };

    async function savePassword() {
        setNewPasswordValid({ valid: true, message: '' });
        setCurrentPasswordValid({ valid: true, message: '' });
        if (newPassword !== newPasswordRepeat) {
            setNewPasswordValid({ valid: false, message: translate('profile.errorPasswordsNotEqual') });
        } else if (newPassword.length < 8) {
            setNewPasswordValid({ valid: false, message: translate('profile.errorPasswordTooSmall') });
        } else if (newPassword === password) {
            setNewPasswordValid({ valid: false, message: translate('profile.errorPasswordOldSameAsNew') });
        } else {
            setIsSubmitting(true);
            const url = '/api/userPassword';
            const result = await fetchPost(url, {}, { currentPassword: password, password: newPassword }, [
                'PASSWORD_NO_PUNCTUATION',
                'PASSWORD_TO_SHORT',
                'WRONG_PASSWORD',
            ]);
            if (result.status >= 400) {
                if (result.errorCode === 'PASSWORD_NO_PUNCTUATION') {
                    setNewPasswordValid({ valid: false, message: translate('profile.errorNoPunctuation') });
                } else if (result.errorCode === 'WRONG_PASSWORD') {
                    setCurrentPasswordValid({ valid: false, message: translate('profile.errorPasswordNotCorrect') });
                } else {
                    setNewPasswordValid({ valid: false, message: translate('common.permissionError') });
                }
            } else {
                props.onClose();
                toast.info(translate('profile.messageChanged'), { autoClose: 3000 });
            }
            setIsSubmitting(false);
        }
    }

    return (
        <Modal id="copy-dialog" classNames="confirm-dialog" closeOnBackdropClick={true} onClose={() => handleCloseFormClicked()}>
            <Modal.Title>
                <h4>{translate('profile.changePassword')}</h4>
            </Modal.Title>
            <Modal.Content isScrollBarVisible={true} style={{ height: '200px' }}>
                <div className="admin-form theme-primary">
                    {(!newPasswordValid.valid || !currentPasswordValid.valid) && (
                        <EditPanelSection>
                            <AlertMicro severity="danger" text={!newPasswordValid.valid ? newPasswordValid.message : currentPasswordValid.message} />
                        </EditPanelSection>
                    )}
                    <EditPanelRow>
                        <EditPanelCell rowCount={6}>
                            <EditPanelInputPassword
                                name="password"
                                autoComplete="current-password"
                                placeholder={translate('profile.currentPassword')}
                                notValid={!currentPasswordValid.valid}
                                value={password}
                                handleChange={(value) => setPassword(value)}
                            />
                        </EditPanelCell>
                    </EditPanelRow>
                    <EditPanelRow>
                        <EditPanelCell rowCount={6}>
                            <EditPanelInputPassword
                                name="password"
                                autoComplete="new-password"
                                placeholder={translate('profile.newPassword')}
                                notValid={!newPasswordValid.valid}
                                value={newPassword}
                                handleChange={(value) => setNewPassword(value)}
                            />
                        </EditPanelCell>
                        <EditPanelCell rowCount={6}>
                            <EditPanelInputPassword
                                name="password"
                                autoComplete="new-password"
                                placeholder={translate('profile.repeatNewPassword')}
                                notValid={!newPasswordValid.valid}
                                value={newPasswordRepeat}
                                handleChange={(value) => setNewPasswordRepeat(value)}
                            />
                        </EditPanelCell>
                    </EditPanelRow>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => handleCloseFormClicked()} />
                <EditPanelButton small={true} primary={true} text={translate('common.save')} disabled={isSubmitting} onClick={savePassword} />
            </Modal.Actions>
        </Modal>
    );
}
